import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, Bell, Crown, Gift, Globe, HeadphonesIcon, LayoutDashboard, Link2, Settings, Wallet } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/App";
import { UserSidebar } from "@/components/shell/UserSidebar";
import { UserTopbar } from "@/components/shell/UserTopbar";
import "@/styles/user-shell.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(() => localStorage.getItem("wizardx-sidebar") !== "collapsed");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [globalBlocks, setGlobalBlocks] = useState({ count: null, status: "loading" });
  const { user, token, logout, isSuperAdmin, isLicensed } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchUnreadCount = useCallback(async () => {
    if (!token || isSuperAdmin || !isLicensed) return;
    try {
      const response = await axios.get(`${API}/user/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadCount((response.data.notifications || []).filter((notification) => !notification.read).length);
    } catch { /* Notification count is non-blocking. */ }
  }, [token, isSuperAdmin, isLicensed]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const fetchGlobalBlocks = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/dashboard/global-blocks-24h`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGlobalBlocks({ count: Number(response.data.total_blocked || 0), status: "live" });
    } catch {
      setGlobalBlocks((current) => ({ ...current, status: "unavailable" }));
    }
  }, [token]);

  useEffect(() => {
    fetchGlobalBlocks();
    const interval = setInterval(fetchGlobalBlocks, 60000);
    return () => clearInterval(interval);
  }, [fetchGlobalBlocks]);

  useEffect(() => {
    setMobileMenuOpen(false);
    if (location.pathname === "/notifications") setUnreadCount(0);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const handler = (event) => { if (event.key === "Escape") setMobileMenuOpen(false); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", handler); document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const unlicensedItems = [
    { path: "/settings", icon: Settings, label: "Account" },
    { path: "/referral", icon: Gift, label: "Referral Plan", highlight: true },
    { path: "/support", icon: HeadphonesIcon, label: "Support" },
    { path: "/billing", icon: Wallet, label: "Billing" },
  ];
  const items = isSuperAdmin ? [
    { path: "/admin", icon: Crown, label: "Admin Panel", highlight: true },
  ] : !isLicensed ? unlicensedItems : [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/geo", icon: Globe, label: "Geo Access" },
    { path: "/redirector", icon: Link2, label: "Links" },
    { path: "/insights", icon: BarChart3, label: "Insights" },
    { path: "/referral", icon: Gift, label: "Referral Plan", highlight: true },
    { path: "/support", icon: HeadphonesIcon, label: "Support" },
    { path: "/billing", icon: Wallet, label: "Billing" },
    { path: "/notifications", icon: Bell, label: "Notifications" },
    { path: "/settings", icon: Settings, label: "Settings" },
  ];
  const handleLogout = () => { logout(); navigate("/login"); };
  const toggleSidebar = () => {
    setSidebarExpanded((current) => {
      const next = !current;
      localStorage.setItem("wizardx-sidebar", next ? "expanded" : "collapsed");
      return next;
    });
  };

  return (
    <div className="user-shell-atmosphere flex min-h-screen min-w-0 overflow-x-clip text-slate-100" data-testid="modern-user-shell">
      <UserSidebar items={items} expanded={sidebarExpanded} unreadCount={unreadCount} user={user} isSuperAdmin={isSuperAdmin} onToggle={toggleSidebar} onLogout={handleLogout} />
      {mobileMenuOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-testid="mobile-menu"><button type="button" className="wx-mobile-drawer-backdrop absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} aria-label="Close navigation" data-testid="mobile-menu-backdrop" /><div className="wx-mobile-drawer-panel relative z-10"><UserSidebar mobile items={items} expanded unreadCount={unreadCount} user={user} isSuperAdmin={isSuperAdmin} onNavigate={() => setMobileMenuOpen(false)} onLogout={handleLogout} /></div></div>}
      <div className="flex min-w-0 flex-1 flex-col">
        <UserTopbar pathname={location.pathname} unreadCount={unreadCount} globalBlocks={globalBlocks} onOpenMobile={() => setMobileMenuOpen(true)} user={user} isSuperAdmin={isSuperAdmin} onLogout={handleLogout} />
        <main className="min-w-0 flex-1 overflow-x-hidden" data-testid="user-shell-main"><div className="user-shell-content"><Outlet /></div></main>
      </div>
    </div>
  );
};

export default Layout;